import { ApplicationSummary, Interview, KanbanColumn } from '../types';

export interface InterviewEntry {
  application: ApplicationSummary;
  interview: Interview;
}

export const interviewTimestamp = (interview: Interview): number =>
  new Date(interview.scheduledAt).getTime();

export const isUpcomingInterview = (interview: Interview, now: number = Date.now()): boolean =>
  interview.status === 'SCHEDULED' && interviewTimestamp(interview) >= now;

export const findNextInterview = (
  interviews: Interview[] | undefined,
  now: number = Date.now()
): Interview | null => (interviews ?? []).find((interview) => isUpcomingInterview(interview, now)) ?? null;

export const collectUpcomingInterviews = (
  columns: KanbanColumn[],
  now: number = Date.now()
): InterviewEntry[] => {
  const entries: InterviewEntry[] = [];
  for (const column of columns) {
    for (const application of column.applications) {
      for (const interview of application.scheduledInterviews ?? []) {
        if (isUpcomingInterview(interview, now)) {
          entries.push({ application, interview });
        }
      }
    }
  }
  return entries.sort((a, b) => interviewTimestamp(a.interview) - interviewTimestamp(b.interview));
};

export const computeUpcomingInterviewCount = (
  columns: KanbanColumn[],
  now: number = Date.now()
): number =>
  columns.reduce(
    (total, column) =>
      total +
      column.applications.reduce(
        (sum, application) =>
          sum +
          (application.scheduledInterviews ?? []).filter((interview) =>
            isUpcomingInterview(interview, now)
          ).length,
        0
      ),
    0
  );

export const computeMetrics = (columns: KanbanColumn[], now: number = Date.now()) => {
  let total = 0;
  let offers = 0;
  let rejections = 0;
  let applied = 0;
  for (const column of columns) {
    total += column.applications.length;
    if (column.status === 'OFFER' || column.status === 'HIRED') offers += column.applications.length;
    if (column.status === 'REJECTED') rejections += column.applications.length;
    if (column.status === 'APPLIED') applied += column.applications.length;
  }
  return {
    totalApplications: total,
    upcomingInterviews: computeUpcomingInterviewCount(columns, now),
    offers,
    rejections,
    responseRate: total === 0 ? 0 : (total - applied) / total,
  };
};
